<!-- src/components/Analytics/AnalyticsFatigueSection.vue -->
<template>
  <section class="space-y-8 text-left font-sans">
    
    <!-- Section Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-100 pb-5">
      <div class="flex items-center gap-3">
        <div class="bg-[#20FCF1] w-2.5 h-8 rounded-full"></div>
        <div>
          <h2 class="text-xl font-black text-[#0A1630] uppercase tracking-tight leading-none">Wellness & Fatigue Compliance</h2>
          <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-1.5">Shift Overtime Reviews, Assessments and Active Risk Mitigation</p>
        </div>
      </div>
      <span class="px-3 py-1 bg-[#20FCF1]/10 text-[#0A1630] text-[9px] font-black uppercase tracking-widest rounded-full self-start sm:self-auto">
        Fatigue Safety Controls
      </span>
    </div>

    <!-- Fatigue KPI Row -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-6">
      
      <!-- Total Fatigue Forms -->
      <div class="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between">
        <div class="flex justify-between items-start">
          <div>
            <p class="text-[9px] font-black uppercase text-slate-400 tracking-[0.2em] mb-1">Total Fatigue Forms</p>
            <p v-if="loadingFatigue" class="text-xs font-bold text-slate-400 animate-pulse mt-2">Loading...</p>
            <p v-else class="text-3xl font-black text-[#0A1630] leading-none">{{ totalCount }}</p>
          </div>
          <div class="p-3 bg-[#0A1630]/5 rounded-2xl text-[#0A1630]">
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
        <div class="mt-4 pt-3 border-t border-slate-50 flex items-center gap-2 text-[9px] text-slate-400 font-black uppercase tracking-wider">
          <span class="text-emerald-500">●</span> Cumulative Tracking
        </div>
      </div>

      <!-- Active Management Plans -->
      <div class="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between">
        <div class="flex justify-between items-start">
          <div>
            <p class="text-[9px] font-black uppercase text-slate-400 tracking-[0.2em] mb-1">Active Mitigation Plans</p>
            <p v-if="loadingFatigue" class="text-xs font-bold text-slate-400 animate-pulse mt-2">Loading...</p>
            <p v-else class="text-3xl font-black text-[#0A1630] leading-none">{{ activePlans }}</p>
          </div>
          <div class="p-3 bg-amber-50 rounded-2xl text-amber-500">
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
        </div>
        <div class="mt-4 pt-3 border-t border-slate-50 flex items-center gap-2 text-[9px] text-amber-600 font-black uppercase tracking-wider">
          <span class="text-amber-500">●</span> Mitigation Active
        </div>
      </div>

      <!-- Completed Assessments -->
      <div class="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between">
        <div class="flex justify-between items-start">
          <div>
            <p class="text-[9px] font-black uppercase text-slate-400 tracking-[0.2em] mb-1">Completed Assessments</p>
            <p v-if="loadingFatigue" class="text-xs font-bold text-slate-400 animate-pulse mt-2">Loading...</p>
            <p v-else class="text-3xl font-black text-[#0A1630] leading-none">{{ completedAssessments }}</p>
          </div>
          <div class="p-3 bg-emerald-50 rounded-2xl text-emerald-500">
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        <div class="mt-4 pt-3 border-t border-slate-50 flex items-center gap-2 text-[9px] text-slate-400 font-black uppercase tracking-wider">
          <span class="text-emerald-500">●</span> Compliance Cleared
        </div>
      </div>

    </div>

    <!-- Main Visual Analytical Charts -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
      
      <!-- Submissions by Form Type Doughnut -->
      <div class="bg-slate-50 p-6 md:p-8 rounded-[40px] border border-slate-100 flex flex-col justify-between">
        <div class="mb-4">
          <h3 class="text-xs font-black text-slate-400 uppercase tracking-[0.2em] leading-none">Operational Categorisation</h3>
          <p class="text-[9px] text-slate-400 mt-2 font-bold leading-none">Distribution of shift risk, overtime and travel assessments</p>
        </div>
        
        <div v-if="hasChartData" class="h-64 flex items-center justify-center relative">
          <Doughnut :data="chartData" :options="chartOptions" />
        </div>
        <p v-else class="text-center text-slate-400 py-10 text-[10px] font-black uppercase tracking-widest italic">No fatigue records to display.</p>
      </div>

      <!-- Risk Level Breakdown Doughnut -->
      <div class="bg-white p-6 md:p-8 rounded-[40px] border border-slate-100 flex flex-col justify-between text-left">
        <div class="mb-4">
          <h3 class="text-xs font-black text-slate-400 uppercase tracking-[0.2em] leading-none">Shift Risk Classification</h3>
          <p class="text-[9px] text-slate-400 mt-2 font-bold leading-none">Proportion of high, medium and low risk levels classified</p>
        </div>
        
        <div v-if="hasRiskData" class="h-64 flex items-center justify-center relative">
          <Doughnut :data="riskLevelChartData" :options="chartOptions" />
        </div>
        <p v-else class="text-center text-slate-400 py-10 text-[10px] font-black uppercase tracking-widest italic">No classified risk parameters available.</p>
      </div>

    </div>

    <!-- 📊 INTERACTIVE FATIGUE DRILL-DOWN ANALYTICS PANEL -->
    <div class="bg-white p-6 md:p-8 rounded-[40px] border border-slate-100 text-left space-y-8">
      
      <!-- Panel Header -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-50 pb-4">
        <div>
          <h3 class="text-xs font-black text-slate-400 uppercase tracking-[0.2em] leading-none">Fatigue Compliance Query Panel</h3>
          <p class="text-[9px] text-slate-400 mt-2 font-bold leading-none">Perform live, relational cascading queries to isolate specific shift risk profiles</p>
        </div>
        <span class="px-3.5 py-1.5 bg-[#20FCF1]/10 text-[#0A1630] text-[9px] font-black uppercase tracking-widest rounded-full self-start sm:self-auto">
          Relational Mode Enabled
        </span>
      </div>

      <!-- Relational Filter Toolbar (CASCADING dropdown fields) -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 bg-slate-50 p-5 rounded-3xl border border-slate-100">
        
        <!-- Dropdown 1: Form Class -->
        <div class="flex flex-col gap-1.5">
          <label class="text-[9px] font-black text-[#0A1630] uppercase tracking-wider ml-1">1. Assessment Class</label>
          <select v-model="filters.docType" class="filter-select-light" @change="resetLowerFilters(1)">
            <option value="all">All Classes</option>
            <option value="Fatigue Assessment">Fatigue Assessment</option>
            <option value="Fatigue Management Plan">Fatigue Management Plan</option>
            <option value="Fatigue Risk Assessment">Fatigue Risk Assessment</option>
          </select>
        </div>

        <!-- Dropdown 2: Risk Classification (Cascaded) -->
        <div class="flex flex-col gap-1.5">
          <label class="text-[9px] font-black text-[#0A1630] uppercase tracking-wider ml-1">2. Risk Classification</label>
          <select v-model="filters.riskLevel" :disabled="filters.docType === 'all'" class="filter-select-light" @change="resetLowerFilters(2)">
            <option value="all">All Risk Levels</option>
            <option v-for="level in availableRiskLevels" :key="level" :value="level">
              {{ level }} Risk
            </option>
          </select>
        </div>

        <!-- Dropdown 3: Mitigation Status (Cascaded) -->
        <div class="flex flex-col gap-1.5">
          <label class="text-[9px] font-black text-[#0A1630] uppercase tracking-wider ml-1">3. Status</label>
          <select v-model="filters.status" :disabled="filters.riskLevel === 'all'" class="filter-select-light" @change="resetLowerFilters(3)">
            <option value="all">All Statuses</option>
            <option v-for="status in availableStatuses" :key="status" :value="status">
              {{ status }}
            </option>
          </select>
        </div>

        <!-- Dropdown 4: Target Worker (Cascaded) -->
        <div class="flex flex-col gap-1.5">
          <label class="text-[9px] font-black text-[#0A1630] uppercase tracking-wider ml-1">4. Target Worker</label>
          <select v-model="filters.worker" :disabled="filters.status === 'all'" class="filter-select-light">
            <option value="all">All Workers</option>
            <option v-for="w in availableWorkers" :key="w" :value="w">
              {{ w }}
            </option>
          </select>
        </div>

      </div>

      <!-- Temporal Period RANGE SLIDER Control -->
      <div class="bg-slate-50 p-6 rounded-3xl border border-slate-100 space-y-4">
        <div class="flex justify-between items-center">
          <span class="text-[9px] font-black text-[#0A1630] uppercase tracking-wider ml-1">Adjust Query Compliance Window</span>
          <span class="px-3 py-1 bg-[#0A1630] text-[#20FCF1] text-[9px] font-black uppercase tracking-wider rounded-lg leading-none">
            Selected: {{ sliderLabel }}
          </span>
        </div>
        
        <div class="px-4">
          <input 
            type="range" 
            min="0" 
            max="5" 
            v-model.number="sliderIndex" 
            class="w-full accent-[#0A1630] cursor-pointer"
          />
          <div class="flex justify-between mt-2 text-[8px] font-black text-slate-400 uppercase tracking-widest px-1">
            <span>Past Month</span>
            <span>Past Quarter</span>
            <span>6 Months</span>
            <span>3 Quarters</span>
            <span>Past Year</span>
            <span>All Time</span>
          </div>
        </div>
      </div>

      <!-- Spotlight Summary query results cards -->
      <div class="grid grid-cols-1 md:grid-cols-12 gap-6 bg-[#0A1630] text-white p-8 rounded-[40px] border-b-4 border-[#20FCF1] relative overflow-hidden">
        <div class="md:col-span-4 flex flex-col justify-center items-center md:items-start text-center md:text-left border-b md:border-b-0 md:border-r border-white/10 pb-6 md:pb-0 md:pr-8 shrink-0">
          <p class="text-[9px] font-black uppercase text-[#20FCF1] tracking-[0.2em] mb-2">Spotlight Results</p>
          <div class="flex items-baseline gap-2">
            <h4 class="text-6xl font-black text-[#20FCF1] tracking-tight leading-none">
              {{ filteredRecords.length }}
            </h4>
            <span class="text-xs font-black uppercase tracking-widest text-white">Forms</span>
          </div>
          <p class="text-[10px] font-black uppercase tracking-wider text-slate-400 mt-3">Matching fatigue records in timeline</p>
        </div>

        <div class="md:col-span-8 flex flex-col justify-between space-y-4 text-left md:pl-6">
          <div>
            <h4 class="text-sm font-black uppercase text-[#20FCF1] tracking-wider leading-none mb-3">Downstream Fatigue Insight</h4>
            <p class="text-xs font-semibold text-slate-300 leading-relaxed">
              {{ fatigueInsightText }}
            </p>
          </div>

          <div class="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-4 border-t border-white/5">
            <div>
              <span class="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-1">Status Ratios</span>
              <span class="text-[10px] font-black text-emerald-400 uppercase tracking-tight leading-none block">
                {{ statusBreakdown.complete }} Completed
              </span>
              <span class="text-[10px] font-black text-amber-400 uppercase tracking-tight leading-none block mt-1">
                {{ statusBreakdown.mitigating }} Mitigating
              </span>
            </div>
            <div>
              <span class="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-1">Risk Ratios</span>
              <span class="text-[10px] font-black text-rose-400 uppercase tracking-tight leading-none block">
                {{ riskBreakdown.high }} High Risk
              </span>
              <span class="text-[10px] font-black text-amber-400 uppercase tracking-tight leading-none block mt-1">
                {{ riskBreakdown.medium }} Medium Risk
              </span>
            </div>
            <div class="col-span-2 sm:col-span-1">
              <span class="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-1">Current Focus</span>
              <span class="text-[10px] font-black text-slate-200 uppercase tracking-tight leading-none block">
                {{ currentQueryFocus }}
              </span>
            </div>
          </div>
        </div>
      </div>

    </div>

  </section>
</template>

<script setup>
import { ref, computed, reactive, watch } from 'vue';
import { useUserStore } from '@/stores/userStore';
import { firebaseApp } from '@/firebaseConfig';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement, Colors } from 'chart.js';
import { Doughnut } from 'vue-chartjs';

ChartJS.register(Title, Tooltip, Legend, ArcElement, Colors);

const props = defineProps({
  data: { 
    type: Object, 
    default: () => ({}) 
  }
});

const userStore = useUserStore();
const db = getFirestore(firebaseApp);

const currentClientId = computed(() => userStore.clientId || userStore.userProfile?.clientId);

const fatigueList = ref([]);
const loadingFatigue = ref(false);

const filters = reactive({
  docType: 'all',
  riskLevel: 'all',
  status: 'all',
  worker: 'all'
});

const sliderIndex = ref(5);
const sliderLabels = ['Past Month', 'Past Quarter', 'Past 6 Months', 'Past 3 Quarters', 'Past Year', 'All Time'];
const sliderLabel = computed(() => sliderLabels[sliderIndex.value]);

// Dynamic temporal threshold calculation linked to slider select index
const dateThreshold = computed(() => {
  const now = new Date();
  if (sliderIndex.value === 0) return new Date(now.setMonth(now.getMonth() - 1));
  if (sliderIndex.value === 1) return new Date(now.setMonth(now.getMonth() - 3));
  if (sliderIndex.value === 2) return new Date(now.setMonth(now.getMonth() - 6));
  if (sliderIndex.value === 3) return new Date(now.setMonth(now.getMonth() - 9));
  if (sliderIndex.value === 4) return new Date(now.setFullYear(now.getFullYear() - 1));
  return null;
});

// Resets lower downstream fields when higher cascading selections change
const resetLowerFilters = (stage) => {
  if (stage === 1) {
    filters.riskLevel = 'all';
    filters.status = 'all';
    filters.worker = 'all';
  } else if (stage === 2) {
    filters.status = 'all';
    filters.worker = 'all';
  } else if (stage === 3) {
    filters.worker = 'all';
  }
};

// Fetches live fatigue compliance profiles directly from the unified safety collection name
const fetchLiveFatigue = async () => {
  const clientId = currentClientId.value;
  if (!clientId) return;
  loadingFatigue.value = true;
  try {
    const q = query(collection(db, 'healthsafetyFormSubmissions'), where('clientId', '==', clientId));
    const querySnapshot = await getDocs(q);
    const temp = [];
    querySnapshot.forEach((doc) => {
      const d = doc.data();
      const fid = d.formIdentifier || '';
      
      // Filter the unified documents stream strictly for fatigue form identifiers
      if (fid.toLowerCase().includes('fatigue') || fid.toLowerCase().includes('shift') || fid.toLowerCase().includes('overtime')) {
        let fDate = '';
        if (d.submittedAt) {
          if (d.submittedAt.toDate) {
            fDate = d.submittedAt.toDate().toISOString();
          } else if (typeof d.submittedAt === 'string') {
            fDate = d.submittedAt;
          } else if (d.submittedAt.seconds) {
            fDate = new Date(d.submittedAt.seconds * 1000).toISOString();
          }
        }

        let docType = 'Fatigue Assessment';
        if (fid.toLowerCase().includes('plan')) docType = 'Fatigue Management Plan';
        else if (fid.toLowerCase().includes('risk')) docType = 'Fatigue Risk Assessment';

        let rLevel = d.formData?.riskLevel || d.formData?.riskClassification || d.riskLevel || 'Low';
        const lowerRisk = rLevel.toLowerCase().trim();
        if (lowerRisk.includes('low')) rLevel = 'Low';
        else if (lowerRisk.includes('med')) rLevel = 'Medium';
        else if (lowerRisk.includes('high')) rLevel = 'High';
        else rLevel = 'Low';

        let fStatus = 'Completed';
        if (d.status === 'Active' || d.status === 'Pending' || d.formData?.closedOut === false) {
          fStatus = 'Active Mitigation';
        }

        temp.push({
          id: doc.id,
          worker: d.userName || d.formData?.employeeName || d.formData?.workerName || d.formData?.candidateName || 'Personnel',
          doc: docType,
          date: fDate,
          riskLevel: rLevel,
          status: fStatus
        });
      }
    });
    fatigueList.value = temp;
  } catch (err) {
    console.error("Failed to query live fatigue compliance documents from Firestore:", err);
  } finally {
    loadingFatigue.value = false;
  }
};

watch(currentClientId, (newId) => {
  if (newId) {
    fetchLiveFatigue();
  }
}, { immediate: true });

// Dynamic cascading options based on high-level choices
const availableRiskLevels = computed(() => {
  if (filters.docType === 'all') return [];
  const set = new Set();
  fatigueList.value
    .filter(f => f.doc === filters.docType)
    .forEach(f => { if (f.riskLevel) set.add(f.riskLevel); });
  return Array.from(set);
});

const availableStatuses = computed(() => {
  if (filters.riskLevel === 'all') return [];
  const set = new Set();
  fatigueList.value
    .filter(f => f.doc === filters.docType && f.riskLevel === filters.riskLevel)
    .forEach(f => { if (f.status) set.add(f.status); });
  return Array.from(set);
});

const availableWorkers = computed(() => {
  if (filters.status === 'all') return [];
  const set = new Set();
  fatigueList.value
    .filter(f => f.doc === filters.docType && f.riskLevel === filters.riskLevel && f.status === filters.status)
    .forEach(f => { if (f.worker && f.worker !== 'N/A') set.add(f.worker); });
  return Array.from(set);
});

// Reactive cascading record filter evaluation
const filteredRecords = computed(() => {
  return fatigueList.value.filter(rec => {
    const matchesDocType = filters.docType === 'all' || rec.doc === filters.docType;
    const matchesRiskLevel = filters.riskLevel === 'all' || rec.riskLevel === filters.riskLevel;
    const matchesStatus = filters.status === 'all' || rec.status === filters.status;
    const matchesWorker = filters.worker === 'all' || rec.worker === filters.worker;
    const matchesDate = !dateThreshold.value || (rec.date && new Date(rec.date) >= dateThreshold.value);
    
    return matchesDocType && matchesRiskLevel && matchesStatus && matchesWorker && matchesDate;
  });
});

const statusBreakdown = computed(() => {
  const records = filteredRecords.value;
  const complete = records.filter(r => r.status === 'Completed').length;
  return {
    complete,
    mitigating: records.length - complete
  };
});

const riskBreakdown = computed(() => {
  const records = filteredRecords.value;
  const high = records.filter(r => r.riskLevel === 'High').length;
  const medium = records.filter(r => r.riskLevel === 'Medium').length;
  return { high, medium };
});

const currentQueryFocus = computed(() => {
  if (filters.worker !== 'all') return filters.worker;
  if (filters.status !== 'all') return filters.status;
  if (filters.riskLevel !== 'all') return filters.riskLevel + ' Risk';
  if (filters.docType !== 'all') return filters.docType;
  return 'Global Fatigue Compliance';
});

// Relational insight generator detailing dynamic results of queries
const fatigueInsightText = computed(() => {
  const count = filteredRecords.value.length;
  const period = sliderLabel.value.toLowerCase();
  
  if (count === 0) {
    return `No active fatigue compliance logs match the selected cascading parameters registered during the ${period}. Risk indexes remain stable.`;
  }
  
  let desc = `Drill-down identifies exactly ${count} active fatigue review${count > 1 ? 's' : ''} mapped in the ${period}. `;
  if (filters.docType !== 'all') {
    desc += `These parameters relate strictly to the ${filters.docType} workflow. `;
  }
  if (filters.riskLevel !== 'all') {
    desc += `Operational alerts classify the safety profile under ${filters.riskLevel} Risk parameters. `;
  }
  if (statusBreakdown.value.mitigating > 0) {
    desc += `Currently, ${statusBreakdown.value.mitigating} shift plans operate under active mitigation parameters to protect worker safety.`;
  } else {
    desc += `All matching fatigue assessments are verified, compliant, and completed.`;
  }
  return desc;
});

const totalCount = computed(() => {
  if (fatigueList.value.length > 0) return fatigueList.value.length;
  const total = props.data?.totalForms ?? props.data?.total ?? 15;
  return isNaN(Number(total)) ? 0 : Number(total);
});

const activePlans = computed(() => {
  if (fatigueList.value.length > 0) {
    return fatigueList.value.filter(f => f.status === 'Active Mitigation').length;
  }
  return props.data?.activePlans ?? 4;
});

const completedAssessments = computed(() => {
  if (fatigueList.value.length > 0) {
    return fatigueList.value.filter(f => f.status === 'Completed').length;
  }
  return props.data?.completedAssessments ?? 11;
});

const formatIdentifier = (id) => {
  if (!id) return 'Unknown';
  return id
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { 
    legend: { 
      position: 'right',
      labels: {
        font: { size: 9, weight: 'bold', family: 'Inter' },
        usePointStyle: true,
        padding: 15
      }
    } 
  }
};

const hasChartData = computed(() => {
  const dataMap = props.data?.byType || {};
  return Object.keys(dataMap).length > 0 || totalCount.value > 0;
});

const chartData = computed(() => {
  const map = {};
  if (fatigueList.value.length > 0) {
    fatigueList.value.forEach(f => {
      map[f.doc] = (map[f.doc] || 0) + 1;
    });
  } else {
    const dataMap = props.data?.byType || {};
    if (Object.keys(dataMap).length > 0) {
      Object.keys(dataMap).forEach(k => {
        map[formatIdentifier(k)] = dataMap[k];
      });
    } else {
      map['Shift Risk Assessment'] = 6;
      map['Overtime Authorisation'] = 5;
      map['Travel Fatigue Review'] = 4;
    }
  }

  return {
    labels: Object.keys(map),
    datasets: [{ 
      data: Object.values(map),
      backgroundColor: ['#0A1630', '#20FCF1', '#94A3B8'],
      borderWidth: 0,
      hoverOffset: 12
    }],
  };
});

const hasRiskData = computed(() => {
  const dataMap = props.data?.riskLevels || {};
  return Object.keys(dataMap).length > 0 || totalCount.value > 0;
});

const riskLevelChartData = computed(() => {
  const map = {};
  if (fatigueList.value.length > 0) {
    fatigueList.value.forEach(f => {
      map[f.riskLevel] = (map[f.riskLevel] || 0) + 1;
    });
  } else {
    const dataMap = props.data?.riskLevels || {};
    if (Object.keys(dataMap).length > 0) {
      Object.keys(dataMap).forEach(k => {
        map[formatIdentifier(k)] = dataMap[k];
      });
    } else {
      map['Low'] = 9;
      map['Medium'] = 4;
      map['High'] = 2;
    }
  }

  return {
    labels: Object.keys(map),
    datasets: [{ 
      data: Object.values(map),
      backgroundColor: ['#10B981', '#FBBF24', '#EF4444'],
      borderWidth: 0,
      hoverOffset: 12
    }],
  };
});
</script>

<style scoped>
.filter-input-light {
  @apply w-full bg-white border border-slate-200 rounded-2xl px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-[#0A1630] focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all;
}
.filter-select-light {
  @apply w-full bg-white border border-slate-200 rounded-2xl px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-[#0A1630] focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all cursor-pointer appearance-none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%230a1630'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 0.8rem;
}
</style>